import { ResponseArray, ResponseObject } from '@/types/shared'
import livechat from '@/utils/axios/livechat'
import qs from 'query-string'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import {
    ChatRoom,
    ChatUser,
    ChatRoomsResponse,
    ChatMessagesResponse,
} from '../types/response'
import {
    GetChatRoomsPayload,
    GetChatMessagesPayload,
    BlockUserPayload,
} from '../types/payload'

// Helper to decode base64 filters from GokuDataGrid
const decodeFilters = (
    filters: string | undefined
): Record<string, unknown> => {
    if (!filters) return {}
    try {
        const decoded = atob(filters)
        return JSON.parse(decoded)
    } catch {
        return {}
    }
}

// Helper to normalize filter value (handle both single value and array)
const normalizeFilterValue = (
    value: unknown
): string | string[] | undefined => {
    if (value === undefined || value === null || value === '') return undefined
    if (Array.isArray(value)) return value.length > 0 ? value : undefined
    if (typeof value === 'object' && value !== null && 'value' in value) {
        return (value as { value: string }).value
    }
    return value as string
}

// Helper to format date value to ISO string
const formatDateValue = (value: unknown): string | undefined => {
    if (!value) return undefined
    if (typeof value === 'string') return value
    if (value instanceof Date) return value.toISOString()
    if (typeof value === 'object' && value !== null) {
        // Handle dayjs or moment objects
        if (
            'toISOString' in value &&
            typeof (value as any).toISOString === 'function'
        ) {
            return (value as any).toISOString()
        }
    }
    return undefined
}

// Get chat rooms with pagination and filters
// Transforms API response to match GokuDataGrid expected format
export const getChatRooms = async (
    payload: DataGridPayload & GetChatRoomsPayload & { filters?: string }
): Promise<ResponseArray<ChatRoom>> => {
    // Decode base64 filters from GokuDataGrid
    const decodedFilters = decodeFilters(payload.filters)

    // Map dateRange array to created_from and created_to
    const dateRange = (decodedFilters.dateRange as unknown[]) || []
    const [createdFrom, createdTo] = dateRange

    // Map DataGrid payload to API params
    const apiParams = {
        limit: payload.page_size || 20,
        status: normalizeFilterValue(decodedFilters.status),
        channel: normalizeFilterValue(decodedFilters.channel),
        assignee: normalizeFilterValue(decodedFilters.assignee),
        created_from: formatDateValue(createdFrom),
        created_to: formatDateValue(createdTo),
    }

    console.log('[Livechat] decodedFilters:', decodedFilters)
    console.log('[Livechat] apiParams:', apiParams)

    const response = await livechat.get<ChatRoomsResponse>(
        `/chat/rooms?${qs.stringify(apiParams, { skipEmptyString: true, skipNull: true })}`
    )

    const data = response.data

    // Transform to ResponseArray format for GokuDataGrid
    return {
        results: data.rooms || [],
        pagination: {
            total: data.older_count || data.rooms?.length || 0,
            page: payload.page || 1,
            page_size: data.current_limit || 20,
            total_pages: Math.ceil(
                (data.older_count || 1) / (data.current_limit || 20)
            ),
            page_next: data.has_more ? (payload.page || 1) + 1 : 0,
            page_prev: (payload.page || 1) > 1 ? (payload.page || 1) - 1 : 0,
        },
    }
}

// Get chat room detail by ID
// API returns room object directly (not wrapped in result)
export const getChatRoomById = async (roomId: string): Promise<ChatRoom> => {
    const response = await livechat.get<ChatRoom>(`/chat/rooms/${roomId}`)
    return response.data
}

// Get messages for a chat room
// API returns { messages, has_more, older_count, current_limit }
export const getChatMessages = async (
    payload: GetChatMessagesPayload
): Promise<ChatMessagesResponse> => {
    const { roomId, lastMessageId, ...rest } = payload
    // Map lastMessageId to 'last_message_id' for API (snake_case)
    const params = {
        ...rest,
        last_message_id: lastMessageId,
    }
    const response = await livechat.get<ChatMessagesResponse>(
        `/chat/rooms/${roomId}/messages?${qs.stringify(params, { skipNull: true })}`
    )
    return response.data
}

// Get all assignable users (agents and employees)
export const getAssignableUsers = async (): Promise<{ users: ChatUser[] }> => {
    const response = await livechat.get<{ users: ChatUser[] }>('/chat/users')
    return response.data
}

// Get assignable users as select options
export const getAssignableUserOptions = async () => {
    const response = await getAssignableUsers()
    const options = [{ label: 'Unassigned', value: 'unassigned' }]
    if (response?.users) {
        options.push(
            ...response.users.map((user) => ({
                label: user.name,
                value: user.id,
            }))
        )
    }
    return options
}

// Block a user (spam protection)
export const blockUser = async (
    payload: BlockUserPayload
): Promise<ResponseObject<ChatUser>> => {
    const response = await livechat.post('/chat/block-user', payload)
    return response.data
}

// Unblock a user
export const unblockUser = async (
    userId: string
): Promise<ResponseObject<ChatUser>> => {
    const response = await livechat.post(`/chat/unblock-user/${userId}`)
    return response.data
}

// Get presigned URL for file upload
export const getPresignedUrl = async (
    fileName: string
): Promise<{ presignedUrl: string; fileUrl: string }> => {
    const response = await livechat.post<{
        presignedUrl: string
        fileUrl: string
    }>('/files/presigned-url', { fileName })
    return response.data
}

// Upload file using presigned URL pattern
export const uploadFile = async ({
    file,
    prefix = '',
}: {
    file: File
    prefix?: string
}): Promise<{ url: string; fileName: string; fileSize: number }> => {
    const fileName = prefix + file.name

    // Step 1: Get presigned URL from backend
    const { presignedUrl, fileUrl } = await getPresignedUrl(fileName)

    // Step 2: Upload directly to MinIO/S3
    const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': file.type || 'application/octet-stream',
        },
    })

    if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`)
    }

    return {
        url: fileUrl,
        fileName: file.name,
        fileSize: file.size,
    }
}
